import React from 'react'
import TextField from '@mui/material/TextField'
import { Autocomplete } from '@mui/material'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'
import { makeStyles } from '@mui/styles'
import parse from 'autosuggest-highlight/parse'
import throttle from 'lodash/throttle'
import { PlaceType } from './types'
import { useTranslation } from 'react-i18next'
import { Theme } from '@mui/material/styles'

const autocompleteService = { current: null }

const useStyles = makeStyles((theme: Theme) => ({
  input: { margin: theme.spacing(2) },
  icon: {
    color: theme.palette.text.secondary,
    marginRight: theme.spacing(2)
  }
}))

/**
 * Geocode by place id
 * @param placeId
 */
export const geocodeByPlaceId = (
  placeId: string
): Promise<google.maps.GeocoderResult[]> => {
  const geocoder: google.maps.Geocoder = new window.google.maps.Geocoder()

  return new Promise<google.maps.GeocoderResult[]>((resolve, reject) => {
    geocoder.geocode({ placeId }, (results, status) => {
      if (status !== window.google.maps.GeocoderStatus.OK) {
        reject(
          new Error(
            `Geocoding query for a place with an ID of '${placeId}' failed - response status: ${status}`
          )
        )

        return
      }

      results && resolve(results)
    })
  })
}

interface IProps {
  address: string
  deliveryPlace: PlaceType | null
  error: string | null
  onAddressChange: (value: string) => void
  onAddressSelect: (value: PlaceType | null) => void
  label?:string
}

const UFOPlaceAutocomplete = ({
  address,
  deliveryPlace,
  onAddressChange,
  onAddressSelect,
  error,
  label
}: IProps): JSX.Element => {
  const { t } = useTranslation()
  const classes = useStyles()
  const [value, setValue] = React.useState<PlaceType | null>(deliveryPlace)
  const [options, setOptions] = React.useState<PlaceType[]>([])
  const [inputValue, setInputValue] = React.useState(address)

  const fetch = React.useMemo(
    () =>
      throttle(
        (
          request: { input: string },
          callback: (results?: PlaceType[]) => void
        ) => {
          (autocompleteService.current as any).getPlacePredictions(
            request,
            callback
          )
        },
        200
      ),
    []
  )

  React.useEffect(() => {
    let active = true

    if (!autocompleteService.current && (window as any).google) {
      autocompleteService.current = new (window as any).google.maps.places.AutocompleteService()
    }

    if (!autocompleteService.current) {
      return undefined
    }

    if (inputValue === '') {
      setOptions(value ? [value] : [])

      return undefined
    }

    fetch({ input: inputValue }, (results?: PlaceType[]) => {
      if (active) {
        let newOptions = [] as PlaceType[]

        if (value) {
          newOptions = [value]
        }

        if (results) {
          newOptions = [...newOptions, ...results]
        }

        setOptions(newOptions)
      }
    })

    return () => {
      active = false
    }
  }, [value, inputValue, fetch])

  return (
    <Autocomplete
      id="autocomplete"
      getOptionLabel={option =>
        typeof option === 'string' ? option : option.description
      }
      filterOptions={x => x}
      options={options}
      autoComplete
      fullWidth
      includeInputInList
      filterSelectedOptions
      value={value}
      onChange={(event: any, newValue: PlaceType | null) => {
        setOptions(newValue ? [newValue, ...options] : options)
        setValue(newValue)
        onAddressSelect(newValue)
      }}
      onInputChange={(event, newInputValue) => {
        setInputValue(newInputValue)
        onAddressChange(newInputValue)
      }}
      renderInput={params => (
        <TextField
          {...params}
          required
          error={!!error}
          helperText={error}
          id="autocomplete.textField"
          name="autocomplete"
          fullWidth
          variant="outlined"
          label={label || t('bookingCommon:addressInputLabel')}
          placeholder={t('bookingCommon:addressInputPlaceholder')}
          InputLabelProps={{ shrink: true }}
          className={classes.input}

        />
      )}
      renderOption={(props, option) => {
        const matches =
          option.structured_formatting.main_text_matched_substrings || []

        const parts = parse(
          option.structured_formatting.main_text,
          matches.map((match: any) => [
            match.offset,
            match.offset + match.length
          ])
        )

        return (
          <li {...props}>
            <Grid container alignItems="center">
              <Grid item>
                <LocationOnIcon className={classes.icon} />
              </Grid>
              <Grid item xs>
                {parts.map((part, index) => (
                  <span
                    key={index}
                    style={{ fontWeight: part.highlight ? 700 : 400 }}
                  >
                    {part.text}
                  </span>
                ))}
                <Typography variant="body2" color="textSecondary">
                  {option.structured_formatting.secondary_text}
                </Typography>
              </Grid>
            </Grid>
          </li>
        )
      }}
    />
  )
}

export default UFOPlaceAutocomplete
